/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import cx from 'classnames';
import Breadcrumb from '@/components/AZCustomComponent/BreadcrumbSTARC';
import type {
  ContentStackDefaultBrandPage,
  ContentStackPremiumBrandPages,
  ContentStackProductListingPage,
  ContentStackShelfPage,
} from '@/features/contentstack/interface';
import { CMSBanner } from '@/features/contentstack/components/CMSBanner/CMSBanner';
import { CMSBannerCTA } from '@/features/contentstack/components/CMSBannerCTA/CMSBannerCTA';
import { CMSBannerSplit } from '@/features/contentstack/components/CMSBannerSplit/CMSBannerSplit';
import { CMSBlogGrid } from '@/features/contentstack/components/CMSBlogGrid/CMSBlogGrid';
import { CMSButtonGrid } from '@/features/contentstack/components/CMSButtonGrid/CMSButtonGrid';
import { CMSCategoryGrid } from '@/features/contentstack/components/CMSCategoryGrid/CMSCategoryGrid';
import { CMSFaq } from '@/features/contentstack/components/CMSFaq/CMSFaq';
import { CMSFeaturedContent } from '@/features/contentstack/components/CMSFeaturedContent/CMSFeaturedContent';
import { CMSGrid } from '@/features/contentstack/components/CMSGrid/CMSGrid';
import { CMSImageCategory } from '@/features/contentstack/components/CMSImageCategory/CMSImageCategory';
import { CMSImageContent } from '@/features/contentstack/components/CMSImageContent/CMSImageContent';
import { CMSImageContentBackground } from '@/features/contentstack/components/CMSImageContentBackground/CMSImageContentBackground';
import { CMSLogoDescription } from '@/features/contentstack/components/CMSLogoDescription/CMSLogoDescription';
import { CMSMenuList } from '@/features/contentstack/components/CMSMenuList/CMSMenuList';
import { CMSProductChart } from '@/features/contentstack/components/CMSProductChart/CMSProductChart';
import { CMSProductTypeGrid } from '@/features/contentstack/components/CMSProductTypeGrid/CMSProductTypeGrid';
import { CMSRichText } from '@/features/contentstack/components/CMSRichText/CMSRichText';
import { CMSSpacer } from '@/features/contentstack/components/CMSSpacer/CMSSpacer';
import { CMSVideoContent } from '@/features/contentstack/components/CMSVideoContent/CMSVideoContent';
import { isValidCMSImageContent } from '@/features/contentstack/utils/isValidCMSImageContent';
import { type IItem } from '@/components/AZCustomComponent/BreadcrumbSTARC/utils/isBreadcrumbModel';
import { Grid } from '@/components/Grid';
import styles from './CMSShelfResultHeaderModularHandler.module.scss';
import Title from '@/components/AZCustomComponent/Title';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import azCommonStyles from '@/theme/globals.module.scss';
import { ExpandableTopDescription } from '../ExpandableTopDescription';
import { useRemoveUnsupportedSearchChars } from '@/hooks/useRemoveUnsupportedSearchChars';
import { SmartLink } from '@/utils/smartLink';
import { View, Icon, Text } from '@az/starc-ui';
import { ArrowLeft } from '@az/starc-ui-icons';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import React from 'react';
import Hidden from '@/components/Hidden';
import { STARCTypography } from '@/components/STARCTypography';
import { CMSShelfSpacer } from '@/features/contentstack/components/CMSShelfSpacer/CMSShelfSpacer';
import { countryCodes } from '@/constants/locale';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { usePageType } from '@/hooks/usePageType';
import { pageTypes } from '@/constants/page';
import { useBrandProductData } from '@/features/brands/api/getProductBrandData';
import { SuperDuperHero } from '@/features/home/components/SuperDuperHero/SuperDuperHero';
import { CMSImageVideoSplit } from '@/features/contentstack/components/CMSImageVideoSplit/CMSImageVideoSplit';
import { CMSGridBanner } from '@/features/contentstack/components/CMSGridBanner/CMSGridBanner';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useACESBrandProductData } from '@/features/brands/api/getAcesProductBrandData';
import { OptionalSuspense } from '@/features/performance/components/OptionalSuspense';
import { getCMSModularBlockId } from '@/features/contentstack/utils/getCMSModularBlockId';
import { CMSPencilBanner } from '@/features/contentstack/components/CMSPencilBanner/CMSPencilBanner';

type Props = {
  catLabel?: string;
  content:
    | NonNullable<ContentStackShelfPage['shelfresultheader']>
    | NonNullable<ContentStackDefaultBrandPage['brandshelfheader']>
    | NonNullable<ContentStackPremiumBrandPages['brandshelfheader']>
    | NonNullable<ContentStackProductListingPage['shelfresultheader']>;
  seoTopCopy?: string | null;
  shelfBreadcrumb?: IItem[];
  shortDescription?: string | null;
  showSEOData: boolean;
  showBackButton?: boolean;
  handleClick?: () => void;
  viewingPartTypes?: boolean;
  setViewingPartTypes?: React.Dispatch<React.SetStateAction<boolean>>;
  headerClassName?: string;
};

const getComponentId = (modularBlock: Record<string, unknown>, index: number): string => {
  if ('component_id' in modularBlock) {
    const componentId = modularBlock.component_id;
    if (typeof componentId === 'string') {
      return componentId;
    }
  }

  const keys = Object.keys(modularBlock);
  for (const key of keys) {
    const value = modularBlock[key];
    if (Array.isArray(value) && value.length > 0) {
      const firstItem = value[0] as unknown;
      if (
        typeof firstItem === 'object' &&
        firstItem !== null &&
        'component_id' in firstItem &&
        typeof (firstItem as Record<string, unknown>).component_id === 'string'
      ) {
        return (firstItem as Record<string, unknown>).component_id as string;
      }
    }
  }

  return `block-${index}`;
};

export const CMSShelfResultHeaderModularHandler = ({
  catLabel,
  content,
  seoTopCopy,
  shelfBreadcrumb = [],
  shortDescription,
  showSEOData,
  showBackButton = false,
  handleClick,
  headerClassName,
}: Props) => {
  const isLargeScreen = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const removeUnsupportedSearchChars = useRemoveUnsupportedSearchChars();
  const { backButtonLabel, label_brandShelf_title_brandNameModifier } = useLabels({
    backButtonLabel: 'label_back_to_results',
    label_brandShelf_title_brandNameModifier: 'label_brandShelf_title_brandNameModifier',
  });
  const locale = useLocale();
  const isUS = locale === countryCodes.enUS;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const useNewShelfDesignMobile =
    useMonetateDecisionFlag('useNewShelfDesignMobile') && isMobile && isUS;
  const router = useLegacyRouter();
  const { pageType } = usePageType();
  const isBrandPage = pageType === pageTypes.BrandShelf;
  const isProductShelf = pageType === pageTypes.ProductShelf;
  const { acesEnabled } = useAcesFlags();
  const useBrandProductDataFunction = acesEnabled ? useACESBrandProductData : useBrandProductData;
  const { data: brandData } = useBrandProductDataFunction({
    enabled: isBrandPage,
  });
  const catLabelValue = isBrandPage
    ? // brandData can be undefined at runtime when query hasn't loaded, despite TypeScript narrowing
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      `${brandData?.brandName ?? ''} ${label_brandShelf_title_brandNameModifier}`.trim()
    : (catLabel ?? '');

  const blockElements = content.map((modularBlock, index) => {
    const getBlockId = (blockName: string) => getCMSModularBlockId(modularBlock, blockName);
    if (modularBlock.breadcrumb?.enable) {
      return (
        <div id={getBlockId('breadcrumb')} key={index}>
          <Hidden smDown implementation="js">
            <Breadcrumb
              customClass={cx({ [styles.showingBack]: showBackButton && !isMobile })}
              showSEOData={showSEOData}
              data={shelfBreadcrumb}
            />
          </Hidden>
          {showBackButton && (
            <SmartLink
              samePage
              to={`/searchresult?searchText=${encodeURIComponent(
                removeUnsupportedSearchChars(String(router.query.searchText)),
              )}`}
              handleClick={handleClick}
            >
              <View align="center" gap={1} direction="row" padding={[2, !isLargeScreen ? 3 : 0]}>
                <Icon svg={ArrowLeft} color="accent" />
                <Text
                  // TODO: remove className when STARC resolves https://gitlab.autozone.com/it/open-interface/starc/-/issues/27
                  className={styles.fontFamCond}
                  weight="medium"
                  attributes={{
                    style: { lineHeight: useNewShelfDesignMobile ? '24px' : '1.40' },
                  }}
                  size={useNewShelfDesignMobile ? '087' : '125'}
                  textCase="uppercase"
                >
                  {backButtonLabel}
                </Text>
              </View>
            </SmartLink>
          )}
          <Hidden mdUp implementation="js">
            <Breadcrumb showSEOData={showSEOData} data={shelfBreadcrumb} />
          </Hidden>
        </div>
      );
    }
    if (modularBlock.banner?.banner?.[0]?.asset_size) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <STARCTypography>
            <CMSShelfSpacer
              removeHorizontalSpacing={{ s: true, l: true }}
              id={getBlockId('banner')}
            >
              <CMSBanner
                content={modularBlock.banner.banner[0]}
                legalPadding={{ s: true, m: true, l: false }}
              />
            </CMSShelfSpacer>
          </STARCTypography>
        </OptionalSuspense>
      );
    }
    if (modularBlock.banner_split?.banner_split?.[0]) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <STARCTypography>
            <CMSShelfSpacer removeHorizontalSpacing={{ l: true }} id={getBlockId('banner_split')}>
              <CMSBannerSplit
                content={modularBlock.banner_split.banner_split[0]}
                cmsBannerOptions={{ legalPadding: { s: true, m: true, l: false } }}
              />
            </CMSShelfSpacer>
          </STARCTypography>
        </OptionalSuspense>
      );
    }

    if (
      'rich_text_editor' in modularBlock &&
      modularBlock.rich_text_editor?.rich_text_editor?.[0]
    ) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSSpacer id={getBlockId('rich_text_editor')}>
            <CMSRichText content={modularBlock.rich_text_editor.rich_text_editor[0]} />
          </CMSSpacer>
        </OptionalSuspense>
      );
    }

    if ('video_content' in modularBlock && modularBlock.video_content?.video_content?.[0]) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSSpacer removeHorizontalSpacing={{ l: true }} id={getBlockId('video_content')}>
            <CMSVideoContent content={modularBlock.video_content.video_content[0]} />
          </CMSSpacer>
        </OptionalSuspense>
      );
    }

    if (
      'image_video_split' in modularBlock &&
      modularBlock.image_video_split?.image_video_split?.[0]
    ) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSSpacer
            removeHorizontalSpacing={{ l: true }}
            removeImagePadding={{ s: true }}
            id={getBlockId('image_video_split')}
          >
            <CMSImageVideoSplit content={modularBlock.image_video_split.image_video_split[0]} />
          </CMSSpacer>
        </OptionalSuspense>
      );
    }

    if ('grid_banner' in modularBlock && modularBlock.grid_banner?.grid_banner?.[0]) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSSpacer removeHorizontalSpacing={{ l: true, s: true }} id={getBlockId('grid_banner')}>
            <CMSGridBanner content={modularBlock.grid_banner.grid_banner[0]} />
          </CMSSpacer>
        </OptionalSuspense>
      );
    }

    if ('faq' in modularBlock && modularBlock.faq?.faq?.[0]?.faq?.length) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer id={getBlockId('faq')}>
            <CMSFaq content={modularBlock.faq.faq[0]} />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    if ('parttype_grid' in modularBlock && modularBlock.parttype_grid?.parttype_grid?.[0]) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer removeHorizontalSpacing={{ l: true }} id={getBlockId('parttype_grid')}>
            <CMSGrid content={modularBlock.parttype_grid.parttype_grid[0]} />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    if ('category_grid' in modularBlock && modularBlock.category_grid?.category_grid?.[0]) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer id={getBlockId('category_grid')}>
            <CMSCategoryGrid content={modularBlock.category_grid.category_grid[0]} />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    if ('product_chart' in modularBlock && modularBlock.product_chart?.product_chart?.[0]) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer id={getBlockId('product_chart')}>
            <CMSProductChart content={modularBlock.product_chart.product_chart[0]} />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    if ('video_content' in modularBlock && modularBlock.video_content?.video_content?.[0]) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer id={getBlockId('video_content')}>
            <CMSVideoContent content={modularBlock.video_content.video_content[0]} />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }
    if (
      'image_content' in modularBlock &&
      modularBlock.image_content?.image_content?.[0] &&
      isValidCMSImageContent(modularBlock.image_content.image_content[0])
    ) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer
            removeHorizontalSpacing={{ l: true }}
            removeImagePadding={{ s: true }}
            id={getBlockId('image_content')}
          >
            <CMSImageContent content={modularBlock.image_content.image_content[0]} />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    if (
      'image_content_background' in modularBlock &&
      modularBlock.image_content_background?.image_content_background?.[0]
    ) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer id={getBlockId('image_content_background')}>
            <CMSImageContentBackground
              content={modularBlock.image_content_background.image_content_background[0]}
            />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    if (
      'image_category_grid' in modularBlock &&
      modularBlock.image_category_grid?.image_category_grid?.[0]
    ) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer id={getBlockId('image_category_grid')}>
            <CMSImageCategory content={modularBlock.image_category_grid.image_category_grid[0]} />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    if ('menu_list' in modularBlock && modularBlock.menu_list?.menu_list?.[0]) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer id={getBlockId('menu_list')}>
            <CMSMenuList content={modularBlock.menu_list.menu_list[0]} />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    if ('product_grid' in modularBlock && modularBlock.product_grid?.product_grid?.[0]) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer id={getBlockId('product_grid')}>
            <CMSProductTypeGrid
              content={modularBlock.product_grid.product_grid[0]}
              gridIndex={index}
            />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    if ('banner_cta' in modularBlock && modularBlock.banner_cta?.banner_cta?.[0]) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer id={getBlockId('banner_cta')}>
            <CMSBannerCTA content={modularBlock.banner_cta.banner_cta[0]} priority={index === 0} />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    if (
      'featured_content' in modularBlock &&
      modularBlock.featured_content?.featured_content?.[0]
    ) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer id={getBlockId('featured_content')}>
            <CMSFeaturedContent content={modularBlock.featured_content.featured_content[0]} />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    if (
      'logo_description' in modularBlock &&
      modularBlock.logo_description?.logo_description?.[0]
    ) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer id={getBlockId('logo_description')}>
            <CMSLogoDescription content={modularBlock.logo_description.logo_description[0]} />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    if ('button_grid' in modularBlock && modularBlock.button_grid?.button_grid?.[0]) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer removeHorizontalSpacing id={getBlockId('button_grid')}>
            <CMSButtonGrid content={modularBlock.button_grid.button_grid[0]} />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    if (
      'super_duper_hero' in modularBlock &&
      modularBlock.super_duper_hero?.super_duper_hero?.[0]
    ) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer removeHorizontalSpacing id={getBlockId('super_duper_hero')}>
            <SuperDuperHero options={modularBlock.super_duper_hero.super_duper_hero[0]} />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    if ('blogtype_grid' in modularBlock && modularBlock.blogtype_grid?.blogtype_grid?.[0]) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer id={getBlockId('blogtype_grid')}>
            <CMSBlogGrid content={modularBlock.blogtype_grid.blogtype_grid[0]} />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    if ('pencil_banner' in modularBlock && modularBlock.pencil_banner?.pencil_banner?.[0]) {
      const blockKey = getComponentId(modularBlock, index);
      return (
        <OptionalSuspense key={blockKey}>
          <CMSShelfSpacer
            removeHorizontalSpacing={{ s: true, l: true }}
            {...(modularBlock.pencil_banner.pencil_banner[0].component_id && {
              id: modularBlock.pencil_banner.pencil_banner[0].component_id,
            })}
          >
            <CMSPencilBanner content={modularBlock.pencil_banner.pencil_banner[0]} />
          </CMSShelfSpacer>
        </OptionalSuspense>
      );
    }

    return null;
  });

  if (!blockElements.some(Boolean) && !catLabelValue) {
    return null;
  }

  const inner = (
    <>
      {blockElements}
      {catLabelValue && (
        <Grid
          container
          spacing={0}
          className={cx(styles.titleSection, {
            [styles.slimTitleSection]: useNewShelfDesignMobile && !isProductShelf,
          })}
          wrap={isLargeScreen ? 'wrap' : 'nowrap'}
        >
          <Grid item sm={12} md={10} lg={9}>
            <Title
              overRuleH1="h1"
              variant="lg"
              firstText={catLabelValue}
              firstTextClass={styles.shelfTitle}
              dataTestIdContainer="custom-header-container"
              id="shelfHeader"
            />
            {shortDescription && seoTopCopy === null && (
              <p className={cx(azCommonStyles['az-body-2-regular'], styles.shortDescription)}>
                {shortDescription}
              </p>
            )}
            {seoTopCopy && <ExpandableTopDescription content={seoTopCopy} />}
          </Grid>
        </Grid>
      )}
    </>
  );

  return headerClassName ? <View className={headerClassName}>{inner}</View> : inner;
};
